package com.schoolconnect.app.feature.schoolusers

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogInputSize
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSuccess
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import org.jetbrains.compose.resources.painterResource

private val ViewSchoolUserDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors

@Composable
fun ViewSchoolUserModal(
    open: Boolean,
    user: SchoolUserRowUi?,
    onClose: () -> Unit
) {
    if (!open || user == null) return

    val roleLabel = when (user.role) {
        SchoolUserRoleUi.SCHOOL_ADMIN -> "School Admin"
        SchoolUserRoleUi.SCHOOL_MANAGEMENT -> "School Management"
    }
    val genderLabel = schoolUserGenderDisplayLabel(user.gender)
    val (statusLabel, statusColor) = when (user.status) {
        SchoolUserStatusUi.ACTIVE -> "Active" to AppSuccess
        SchoolUserStatusUi.INACTIVE -> "Inactive" to AppDanger
    }

    AppDialogScaffold(
        onDismiss = onClose,
        size = AppDialogSize.Standard,
        colors = ViewSchoolUserDialogColors,
        dismissOnClickOutside = true
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            ViewSchoolUserHeader(user = user, onClose = onClose)
            AppDialogDivider(colors = ViewSchoolUserDialogColors)
            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                val gap = 16.dp
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(gap)
                ) {
                    if (compact) {
                        ViewSchoolUserField(label = "School", value = user.school)
                        ViewSchoolUserField(label = "Full Name", value = user.name)
                        ViewSchoolUserField(label = "Staff Role", value = user.staffRole)
                        ViewSchoolUserField(label = "Mobile Number", value = user.mobile)
                        ViewSchoolUserField(label = "Email Address", value = user.email)
                        ViewSchoolUserField(label = "Staff ID", value = user.staffId)
                        ViewSchoolUserField(label = "Role", value = roleLabel)
                        ViewSchoolUserField(label = "Gender", value = genderLabel)
                        ViewSchoolUserField(label = "Status", value = statusLabel, valueColor = statusColor)
                    } else {
                        ViewSchoolUserFieldRow(
                            leftLabel = "School",
                            leftValue = user.school,
                            rightLabel = "Full Name",
                            rightValue = user.name
                        )
                        ViewSchoolUserFieldRow(
                            leftLabel = "Staff Role",
                            leftValue = user.staffRole,
                            rightLabel = "Mobile Number",
                            rightValue = user.mobile
                        )
                        ViewSchoolUserFieldRow(
                            leftLabel = "Email Address",
                            leftValue = user.email,
                            rightLabel = "Staff ID",
                            rightValue = user.staffId
                        )
                        ViewSchoolUserFieldRow(
                            leftLabel = "Role",
                            leftValue = roleLabel,
                            rightLabel = "Gender",
                            rightValue = genderLabel
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            ViewSchoolUserField(
                                modifier = Modifier.weight(1f),
                                label = "Status",
                                value = statusLabel,
                                valueColor = statusColor
                            )
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
            AppDialogFooter(
                actions = listOf(
                    AppDialogFooterAction(
                        label = "Close",
                        style = AppDialogActionStyle.Primary,
                        onClick = onClose
                    )
                ),
                colors = ViewSchoolUserDialogColors,
                contentPadding = PaddingValues(horizontal = 24.dp)
            )
        }
    }
}

@Composable
private fun ViewSchoolUserHeader(
    user: SchoolUserRowUi,
    onClose: () -> Unit
) {
    val createdBy = user.createdBy.ifBlank { "—" }
    val updatedBy = user.updatedBy.ifBlank { "—" }
    val createdOn = user.createdOn.ifBlank { "—" }
    val updatedOn = user.updatedOn.ifBlank { "—" }
    val auditText = buildAnnotatedString {
        val labelStyle = SpanStyle(fontWeight = FontWeight.SemiBold, color = AppMuted)
        val valueStyle = SpanStyle(fontWeight = FontWeight.Normal, color = AppMuted)
        withStyle(labelStyle) { append("Created by") }
        withStyle(valueStyle) { append(" $createdBy · $createdOn | ") }
        withStyle(labelStyle) { append("Last modified by") }
        withStyle(valueStyle) { append(" $updatedBy · $updatedOn") }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 20.dp, top = 16.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = "View School User",
                color = AppPrimary,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                maxLines = 1
            )
            Text(
                text = "School user details and access information.",
                color = AppMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.Normal,
                maxLines = 2
            )
            Text(
                text = auditText,
                fontSize = 12.sp,
                lineHeight = 18.sp
            )
        }
        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = onClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = "Close",
                tint = AppMuted,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun ViewSchoolUserFieldRow(
    leftLabel: String,
    leftValue: String,
    rightLabel: String,
    rightValue: String,
    leftValueColor: Color? = null,
    rightValueColor: Color? = null
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ViewSchoolUserField(
            modifier = Modifier.weight(1f),
            label = leftLabel,
            value = leftValue,
            valueColor = leftValueColor
        )
        ViewSchoolUserField(
            modifier = Modifier.weight(1f),
            label = rightLabel,
            value = rightValue,
            valueColor = rightValueColor
        )
    }
}

@Composable
private fun ViewSchoolUserField(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: Color? = null
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = label,
            color = AppNavy,
            fontSize = AppDialogLabelSize,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            text = value.ifBlank { "—" },
            color = valueColor ?: AppNavy,
            fontSize = AppDialogInputSize,
            fontWeight = FontWeight.Normal
        )
    }
}
